import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import D3EFeature from "../models/D3EFeature";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import IconView from "./IconView";
import Divider from "./Divider";
import { BuildContext } from "../classes/BuildContext";

type _AppFeatureListTileViewOnAddFeature = (item: D3EFeature) => void;

type _AppFeatureListTileViewOnRemoveFeature = (item: D3EFeature) => void;

type _AppFeatureListTileViewOnViewFeature = (item: D3EFeature) => void;

type _ViewFeatureButtonOnPressed = (
  d3eState: AppFeatureListTileViewRefs
) => void;

type _AddFeatureButtonOnPressed = (
  d3eState: AppFeatureListTileViewRefs
) => void;

type _RemoveFeatureButtonOnPressed = (
  d3eState: AppFeatureListTileViewRefs
) => void;

export interface AppFeatureListTileViewProps extends BaseUIProps {
  key?: string;
  obj: D3EFeature;
  isAddedFeature: boolean;
  onAddFeature?: _AppFeatureListTileViewOnAddFeature;
  onRemoveFeature?: _AppFeatureListTileViewOnRemoveFeature;
  onViewFeature?: _AppFeatureListTileViewOnViewFeature;
}
/// To store state data for AppFeatureListTileView
class AppFeatureListTileViewRefs {
  public addFeatureButton: AddFeatureButtonState = new AddFeatureButtonState();
  public removeFeatureButton: RemoveFeatureButtonState =
    new RemoveFeatureButtonState();
  public viewFeatureButton: ViewFeatureButtonState =
    new ViewFeatureButtonState();
}

interface RemoveFeatureButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AppFeatureListTileViewRefs;
  _onRemoveFeatureHandler?: _RemoveFeatureButtonOnPressed;
}

class RemoveFeatureButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _RemoveFeatureButtonWithState extends ObservableComponent<RemoveFeatureButtonWithStateProps> {
  removeFeatureButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: RemoveFeatureButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get removeFeatureButton(): RemoveFeatureButtonState {
    return this.props.d3eState.removeFeatureButton;
  }
  public get d3eState(): AppFeatureListTileViewRefs {
    return this.props.d3eState;
  }
  public get _onRemoveFeatureHandler(): _RemoveFeatureButtonOnPressed {
    return this.props._onRemoveFeatureHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable(
      "removeFeatureButton",
      null,
      this.removeFeatureButton
    );

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["removeFeatureButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      child: Button({
        padding: ui.EdgeInsets.all(10.0, new Map()),
        decoration: cStyle.tButtonOutlineButtonDecorationOn,
        disable: this.removeFeatureButton.disable,
        onPressed: () => {
          this._onRemoveFeatureHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: IconView({
          icon: MaterialIcons.delete,
          size: 16,
          className: "xaf",
        }),
      }),
      className: "x68",
    });
  }
}
function RemoveFeatureButtonWithState(
  props: RemoveFeatureButtonWithStateProps
) {
  return React.createElement(_RemoveFeatureButtonWithState, props);
}

interface AddFeatureButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AppFeatureListTileViewRefs;
  _onAddFeatureHandler?: _AddFeatureButtonOnPressed;
}

class AddFeatureButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _AddFeatureButtonWithState extends ObservableComponent<AddFeatureButtonWithStateProps> {
  addFeatureButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AddFeatureButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get addFeatureButton(): AddFeatureButtonState {
    return this.props.d3eState.addFeatureButton;
  }
  public get d3eState(): AppFeatureListTileViewRefs {
    return this.props.d3eState;
  }
  public get _onAddFeatureHandler(): _AddFeatureButtonOnPressed {
    return this.props._onAddFeatureHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("addFeatureButton", null, this.addFeatureButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["addFeatureButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      child: Button({
        padding: ui.EdgeInsets.all(10.0, new Map()),
        decoration: cStyle.tButtonOutlineButtonDecorationOn,
        disable: this.addFeatureButton.disable,
        onPressed: () => {
          this._onAddFeatureHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: IconView({
          icon: MaterialIcons.add,
          size: 16,
          className: "x9c",
        }),
      }),
      className: "xe2",
    });
  }
}
function AddFeatureButtonWithState(props: AddFeatureButtonWithStateProps) {
  return React.createElement(_AddFeatureButtonWithState, props);
}

interface ViewFeatureButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AppFeatureListTileViewRefs;
  _onViewFeatureHandler?: _ViewFeatureButtonOnPressed;
}

class ViewFeatureButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _ViewFeatureButtonWithState extends ObservableComponent<ViewFeatureButtonWithStateProps> {
  viewFeatureButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ViewFeatureButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get viewFeatureButton(): ViewFeatureButtonState {
    return this.props.d3eState.viewFeatureButton;
  }
  public get d3eState(): AppFeatureListTileViewRefs {
    return this.props.d3eState;
  }
  public get _onViewFeatureHandler(): _ViewFeatureButtonOnPressed {
    return this.props._onViewFeatureHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("viewFeatureButton", null, this.viewFeatureButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["viewFeatureButton"], this.rebuild);
  }
  public dispose(): void {
    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
      child: Button({
        padding: ui.EdgeInsets.all(10.0, new Map()),
        decoration: cStyle.tButtonOutlineButtonDecorationOn,
        disable: this.viewFeatureButton.disable,
        onPressed: () => {
          this._onViewFeatureHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: IconView({
          icon: MaterialIcons.remove_red_eye,
          size: 16,
          className: "x78",
        }),
      }),
      className: "xbf",
    });
  }
}
function ViewFeatureButtonWithState(props: ViewFeatureButtonWithStateProps) {
  return React.createElement(_ViewFeatureButtonWithState, props);
}

class _AppFeatureListTileViewState extends ObservableComponent<AppFeatureListTileViewProps> {
  static defaultProps = {
    obj: null,
    isAddedFeature: false,
    onAddFeature: null,
    onRemoveFeature: null,
    onViewFeature: null,
  };
  d3eState: AppFeatureListTileViewRefs = new AppFeatureListTileViewRefs();
  isAddFeature: boolean = false;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AppFeatureListTileViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): D3EFeature {
    return this.props.obj;
  }
  public get isAddedFeature(): boolean {
    return this.props.isAddedFeature;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      [
        "isAddFeature",
        "obj",
        "obj.bundle",
        "obj.bundle.title",
        "obj.icon",
        "obj.title",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AppFeatureListTileViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.isAddedFeature !== this.props.isAddedFeature) {
      this.fire("isAddedFeature", this);
    }
  }
  public setIsAddFeature(val: boolean): void {
    let isValChanged: boolean = this.isAddFeature !== val;

    if (!isValChanged) {
      return;
    }

    this.isAddFeature = val;

    this.fire("isAddFeature", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              children: [
                ui.Column({
                  children: [
                    ui.NetworkImage({
                      url: this.obj.icon?.downloadUrl ?? "",
                      height: 30,
                      width: 30,
                      className: "xda hc vc",
                      key: "0",
                    }),
                  ],
                  key: "0",
                }),
                ui.Column({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    TextView({
                      data: this.obj.title,
                      style: new ui.TextStyle({
                        color: new ui.Color(0xff9b9fb1),
                      }),
                      className: "xb8",
                      key: "0",
                    }),
                    TextView({
                      data:
                        this.obj.bundle !== null ? this.obj.bundle.title : "",
                      style: new ui.TextStyle({ color: cStyle.c13 }),
                      className: "xda8",
                      key: "1",
                    }),
                  ],
                  className: "x42",
                  key: "1",
                }),
              ],
              className: "x9e",
              key: "0",
            }),
            ui.Row({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                ViewFeatureButtonWithState({
                  d3eState: this.d3eState,
                  _onViewFeatureHandler: this.onViewFeatureHandler,
                  key: "0",
                }),
                !this.isAddFeature
                  ? AddFeatureButtonWithState({
                      d3eState: this.d3eState,
                      _onAddFeatureHandler: this.onAddFeatureHandler,
                    })
                  : RemoveFeatureButtonWithState({
                      d3eState: this.d3eState,
                      _onRemoveFeatureHandler: this.onRemoveFeatureHandler,
                    }),
              ],
              key: "1",
            }),
          ],
          className: "x05 hc h",
          key: "0",
        }),
        Divider({ className: "xf2 hc h", key: "1" }),
      ],
      className: ui.join(
        this.props.className,
        "AppFeatureListTileView x99 hc h"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setIsAddFeature(this.isAddedFeature);
  };
  public onAddFeatureHandler = (d3eState: AppFeatureListTileViewRefs): void => {
    this.setIsAddFeature(true);

    this.onAddFeature(this.obj);
  };
  public onRemoveFeatureHandler = (
    d3eState: AppFeatureListTileViewRefs
  ): void => {
    this.setIsAddFeature(false);

    this.onRemoveFeature(this.obj);
  };
  public onViewFeatureHandler = (
    d3eState: AppFeatureListTileViewRefs
  ): void => {
    this.onViewFeature(this.obj);
  };
  public get onAddFeature(): _AppFeatureListTileViewOnAddFeature {
    return this.props.onAddFeature;
  }
  public get onRemoveFeature(): _AppFeatureListTileViewOnRemoveFeature {
    return this.props.onRemoveFeature;
  }
  public get onViewFeature(): _AppFeatureListTileViewOnViewFeature {
    return this.props.onViewFeature;
  }
  public get addFeatureButton() {
    return this.d3eState.addFeatureButton;
  }
  public get removeFeatureButton() {
    return this.d3eState.removeFeatureButton;
  }
  public get viewFeatureButton() {
    return this.d3eState.viewFeatureButton;
  }
}
export default function AppFeatureListTileView(
  props: AppFeatureListTileViewProps
) {
  return React.createElement(_AppFeatureListTileViewState, {
    ..._AppFeatureListTileViewState.defaultProps,
    ...props,
  });
}
